import { useEffect, useState } from "react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Hero from "../components/Hero";
import ScannerTable from "../components/ScannerTable";
import BacktestReport from "../components/BacktestReport";
import Methodology from "../components/Methodology";
import StockDetailModal from "../components/StockDetailModal";
import AlertsPanel from "../components/AlertsPanel";
import TodayPanel from "../components/TodayPanel";
import { fetchScanner, rescanNow, fetchBacktest, rerunBacktest, fetchScannerToday } from "../lib/api";

export default function Landing() {
  const [scan, setScan] = useState(null);
  const [bt, setBt] = useState(null);
  const [today, setToday] = useState(null);
  const [rescanLoading, setRescanLoading] = useState(false);
  const [btLoading, setBtLoading] = useState(false);
  const [openStock, setOpenStock] = useState(null);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 60_000); // refresh every 60s while page is open
    return () => clearInterval(t);
  }, []);

  const loadAll = async () => {
    try {
      const [s, b, td] = await Promise.all([fetchScanner(), fetchBacktest(), fetchScannerToday()]);
      setScan(s);
      setBt(b);
      setToday(td);
    } catch (e) {
      // silent
    }
  };

  const handleRescan = async () => {
    setRescanLoading(true);
    toast.loading("Rescanning NSE equity universe on 1H candles…", { id: "rescan" });
    try {
      const data = await rescanNow();
      setScan(data);
      toast.success(`Found ${data.hits.length} breakouts across ${data.scanned} stocks`, { id: "rescan" });
    } catch (e) {
      toast.error("Rescan failed — check the backend logs", { id: "rescan" });
    } finally {
      setRescanLoading(false);
    }
  };

  const handleRerunBt = async () => {
    setBtLoading(true);
    toast.loading("Re-running backtest — this can take ~1 min…", { id: "bt" });
    try {
      const data = await rerunBacktest();
      setBt(data);
      toast.success(`Backtest complete · ${data.aggregate.total_signals} historical signals`, { id: "bt" });
    } catch (e) {
      toast.error("Backtest failed — please retry", { id: "bt" });
    } finally {
      setBtLoading(false);
    }
  };

  return (
    <div className="relative">
      <Nav />
      <Hero
        scannedCount={scan?.scanned || 0}
        lastRefreshed={scan?.generated_at}
        hits={scan?.hits?.length || 0}
        onScroll={() => document.getElementById("scanner")?.scrollIntoView({ behavior: "smooth" })}
      />
      <ScannerTable
        data={scan}
        rescanLoading={rescanLoading}
        onRescan={handleRescan}
        onOpenStock={(sym) => setOpenStock(sym)}
        todayPanel={<TodayPanel data={today} onOpenStock={(sym) => setOpenStock(sym)} />}
      />
      <BacktestReport
        data={bt}
        rerunLoading={btLoading}
        onRerun={handleRerunBt}
        onOpenStock={(sym) => setOpenStock(sym)}
      />
      <AlertsPanel />
      <Methodology />
      <footer className="border-t border-white/8 py-10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="font-mono text-xs text-white/40 tracking-widest">
            PSYCHLEVEL · NSE EQUITY SCANNER · 1H CANDLES · YFINANCE
          </div>
          <div className="font-mono text-xs text-white/40">
            © {new Date().getFullYear()} · Educational research · Not investment advice.
          </div>
        </div>
      </footer>

      <StockDetailModal symbol={openStock} onClose={() => setOpenStock(null)} />
    </div>
  );
}
